import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './PublisherDailyReport.css';

interface DailyReportData {
  date: string;
  clicks: number;
  conversions: number;
  cvr: string;
  earnings: string;
}

interface OfferReportData {
  offer_id: number;
  offer_name: string;
  clicks: number;
  conversions: number;
  cvr: string;
  earnings: string;
}

interface PublisherDailyReportProps {
  publisherIdOverride?: string;
}

const PublisherDailyReport: React.FC<PublisherDailyReportProps> = ({ publisherIdOverride }) => {
  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [fromDate, setFromDate] = useState<string>(getTodayDate());
  const [toDate, setToDate] = useState<string>(getTodayDate());
  const [reportData, setReportData] = useState<DailyReportData[]>([]);
  const [offerReportData, setOfferReportData] = useState<OfferReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [viewMode, setViewMode] = useState<'daily' | 'offers'>('daily');
  const [selectedDate, setSelectedDate] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      alert('Please select both From Date and To Date');
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      alert('From Date cannot be after To Date');
      return;
    }

    try {
      setLoading(true);
      const publisherId = publisherIdOverride || localStorage.getItem('publisherId');

      if (!publisherId) {
        alert('Publisher ID not found. Please login again.');
        return;
      }

      const response = await apiService.getPublisherPerformance(
        parseInt(publisherId),
        {
          startDate: fromDate,
          endDate: toDate
        }
      );

      setReportData(response.performance);
      setHasSearched(true);
      setViewMode('daily');
    } catch (error: any) {
      console.error('Failed to fetch daily report:', error);
      alert('Failed to load daily report');
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = async (date: string) => {
    try {
      setLoading(true);
      const publisherId = publisherIdOverride || localStorage.getItem('publisherId');

      if (!publisherId) {
        alert('Publisher ID not found. Please login again.');
        return;
      }

      const response = await apiService.getPublisherDateOffersReport(
        parseInt(publisherId),
        date
      );

      setOfferReportData(response.report);
      setSelectedDate(date);
      setViewMode('offers');
    } catch (error: any) {
      console.error('Failed to fetch offer report:', error);
      alert('Failed to load offer report');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDailyReport = () => {
    setViewMode('daily');
    setSelectedDate('');
    setOfferReportData([]);
  };

  return (
    <div className="publisher-daily-report">
      <div className="report-header">
        <h1>Day Wise Report</h1>
      </div>

      <div className="date-filter-section">
        <form onSubmit={handleSubmit} className="date-filter-form">
          <div className="date-inputs">
            <div className="form-group">
              <label htmlFor="fromDate">From Date</label>
              <input
                type="date"
                id="fromDate"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="date-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="toDate">To Date</label>
              <input
                type="date"
                id="toDate"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="date-input"
                required
              />
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Loading...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>

      <div className="report-table-section">
        {viewMode === 'offers' && (
          <div style={{ marginBottom: '15px' }}>
            <button onClick={handleBackToDailyReport} className="submit-btn" style={{ padding: '8px 20px' }}>
              ← Back to Daily Report
            </button>
            <span style={{ marginLeft: '15px', color: '#ecf0f1', fontSize: '16px' }}>
              Offer-wise Report for {new Date(selectedDate).toLocaleDateString()}
            </span>
          </div>
        )}

        {loading ? (
          <div className="loading-message">Loading report data...</div>
        ) : viewMode === 'offers' ? (
          offerReportData.length === 0 ? (
            <div className="no-data-message">No offers found for this date</div>
          ) : (
            <div className="table-container">
              <table className="daily-report-table">
                <thead>
                  <tr>
                    <th>Offer ID</th>
                    <th>Offer Name</th>
                    <th>Clicks</th>
                    <th>Conversions</th>
                    <th>CR%</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {offerReportData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.offer_id}</td>
                      <td>{row.offer_name}</td>
                      <td>{row.clicks.toLocaleString()}</td>
                      <td>{row.conversions.toLocaleString()}</td>
                      <td>{row.cvr}%</td>
                      <td>${row.earnings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : hasSearched && reportData.length === 0 ? (
          <div className="no-data-message">
            No data found for the selected date range
          </div>
        ) : hasSearched && reportData.length > 0 ? (
          <div className="table-container">
            <table className="daily-report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Total Clicks</th>
                  <th>Total Conversions</th>
                  <th>CR%</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDateClick(row.date);
                        }}
                        style={{ color: '#3498db', textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {new Date(row.date).toLocaleDateString()}
                      </a>
                    </td>
                    <td>{row.clicks.toLocaleString()}</td>
                    <td>{row.conversions.toLocaleString()}</td>
                    <td>{row.cvr}%</td>
                    <td>${row.earnings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-search-message">
            Please select a date range and click Submit to view the report
          </div>
        )}
      </div>
    </div>
  );
};

export default PublisherDailyReport;
